import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, FileText, Users as UsersIcon, ListChecks, Smartphone, BookOpen, UserCog, Settings as SettingsIcon, CreditCard, History, Building2, Receipt, Layers } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AccountConfirm from "@/pages/AccountConfirm";
import TenantDashboard from "@/pages/dashboard/UnifiedDashboard";
import Invoices from "@/pages/tenant/Invoices";
import InvoiceDetail from "@/pages/tenant/InvoiceDetail";
import Customers from "@/pages/tenant/Customers";
import Templates from "@/pages/tenant/Templates";
import Payments from "@/pages/tenant/Payments";
import Tickets from "@/pages/tenant/Tickets";
import Accounting from "@/pages/accounting/Accounting";
import Users from "@/pages/tenant/Users";
import Branches from "@/pages/tenant/Branches";
import Settings from "@/pages/tenant/Settings";
import Billing from "@/pages/tenant/Billing";
import PaystackCheckout from "@/pages/tenant/PaystackCheckout";
import BillingCallback from "@/pages/tenant/BillingCallback";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import AuditLog from "@/pages/tenant/AuditLog";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalInvoice from "@/pages/portal/PortalInvoice";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalSetPassword from "@/pages/portal/PortalSetPassword";
import PortalProfile from "@/pages/portal/PortalProfile";
import PortalVerifyEmail from "@/pages/portal/PortalVerifyEmail";
import PortalJobs from "@/pages/portal/PortalJobs";
import PortalJob from "@/pages/portal/PortalJob";
import PortalQuotes from "@/pages/portal/PortalQuotes";
import Reports from "@/pages/reports/Reports";
import PortalLink from "@/pages/portal/PortalLink";
import LegalPage from "@/pages/LegalPage";
import { PortalAuthProvider } from "@/context/PortalAuthContext";
import { BarChart3 } from "lucide-react";
import Pos from "@/pages/pos/Pos";
import Track from "@/pages/pos/Track";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformTenants from "@/pages/platform/PlatformTenants";
import PlatformTenantDetail from "@/pages/platform/PlatformTenantDetail";
import { PlatformPlans, PlatformInvoices } from "@/pages/platform/PlatformMisc";
import PlatformSettings from "@/pages/platform/PlatformSettings";
import { Cog } from "lucide-react";
import Crm from "@/pages/crm/Crm";
import LeadDetail from "@/pages/crm/LeadDetail";
import OpportunityDetail from "@/pages/crm/OpportunityDetail";
import CustomerHistory from "@/pages/crm/CustomerHistory";
import Quotes from "@/pages/quotes/Quotes";
import QuoteBuilder from "@/pages/quotes/QuoteBuilder";
import QuoteDetail from "@/pages/quotes/QuoteDetail";
import Jobs from "@/pages/jobs/Jobs";
import JobDetail from "@/pages/jobs/JobDetail";
import Inventory from "@/pages/inventory/Inventory";
import Procurement from "@/pages/procurement/Procurement";
import Estimating from "@/pages/settings/Estimating";
import DataImport from "@/pages/settings/DataImport";
import QuickBooksMigration from "@/pages/settings/QuickBooksMigration";
import Floor from "@/pages/floor/Floor";
import { Target, Calculator, Factory, Boxes, ScanLine, SlidersHorizontal, Store, Upload, UserRound, CalendarDays, Clock, Network, Wallet } from "lucide-react";
import Employees from "@/pages/hr/Employees";
import { Leave, Attendance } from "@/pages/hr/LeaveAttendance";
import OrgChart from "@/pages/hr/OrgChart";
import Payroll from "@/pages/payroll/Payroll";
import Maintenance from "@/pages/maintenance/Maintenance";
import AssetDetail from "@/pages/maintenance/AssetDetail";
import { Wrench } from "lucide-react";
import { ShieldCheck, Headset, LifeBuoy, Timer, CalendarRange } from "lucide-react";
import Capacity from "@/pages/jobs/Capacity";
import SlaSettings from "@/pages/ops/SlaSettings";
import InternalTeam from "@/pages/platform/InternalTeam";
import OpsConsole from "@/pages/ops/OpsConsole";
import SupportQueue from "@/pages/ops/SupportQueue";
const OPS_NAV = [{ id: "ops-console", to: "/ops", end: true, label: "Ops console", icon: Headset }, { id: "ops-tickets", to: "/ops/tickets", label: "Ticket queue", icon: LifeBuoy }, { id: "ops-sla", to: "/ops/sla", label: "SLA targets", icon: Timer }];
const INTERNAL_ROLES = ["platform_ops", "support_l1", "support_l2"];
const HR_ROLES = ["tenant_admin", "hr_manager", "accountant"];

// Sidebar order = array order; consecutive items sharing `section` render under one muted label.
const TENANT_NAV = [
  { id: "dashboard", to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard },
  { id: "quotes", to: "/app/quotes", label: "Quotations", icon: Calculator, cap: "estimating", section: "Operations" },
  { id: "jobs", to: "/app/jobs", label: "Jobs", icon: Factory, cap: "operations", section: "Operations" },
  { id: "capacity", to: "/app/capacity", label: "Capacity planner", icon: CalendarRange, cap: "operations", roles: ["tenant_admin", "production"], section: "Operations" },
  { id: "floor", to: "/floor", label: "Shop floor", icon: ScanLine, cap: "operations", section: "Operations" },
  { id: "pos", to: "/pos", label: "POS counter", icon: Store, cap: "pos", section: "Operations" },
  { id: "maintenance", to: "/app/maintenance", label: "Maintenance", icon: Wrench, cap: "maintenance", section: "Operations" },
  { id: "crm", to: "/app/crm", label: "CRM", icon: Target, cap: "crm", section: "Clients" },
  { id: "customers", to: "/app/customers", label: "Customers", icon: UsersIcon, section: "Clients" },
  { id: "invoices", to: "/app/invoices", label: "Invoices", icon: FileText, section: "Finance" },
  { id: "payments", to: "/app/payments", label: "Payments", icon: Smartphone, section: "Finance" },
  { id: "templates", to: "/app/templates", label: "Payment templates", icon: ListChecks, section: "Finance" },
  { id: "accounting", to: "/app/accounting", label: "Accounting", icon: BookOpen, cap: "accounting", section: "Finance" },
  { id: "procurement", to: "/app/procurement", label: "Procurement", icon: Receipt, cap: "procurement", section: "Supply chain" },
  { id: "inventory", to: "/app/inventory", label: "Inventory", icon: Boxes, cap: "inventory", section: "Supply chain" },
  { id: "employees", to: "/app/hr/employees", label: "Employees", icon: UserRound, cap: "hr", roles: HR_ROLES, section: "People" },
  { id: "leave", to: "/app/hr/leave", label: "Leave", icon: CalendarDays, cap: "hr", section: "People" },
  { id: "attendance", to: "/app/hr/attendance", label: "Attendance", icon: Clock, cap: "hr", roles: HR_ROLES, section: "People" },
  { id: "org-chart", to: "/app/hr/org-chart", label: "Org chart", icon: Network, cap: "hr", section: "People" },
  { id: "payroll", to: "/app/payroll", label: "Payroll", icon: Wallet, cap: "payroll", roles: HR_ROLES, section: "People" },
  { id: "tickets", to: "/app/tickets", label: "Support tickets", icon: LifeBuoy, section: "Help" },
  // REPORTS SLOT: sits between Finance/Supply chain and Administration. Add further report pages here with section: "Reports".
  { id: "reports", to: "/app/reports", label: "Reports", icon: BarChart3, cap: "reporting", roles: ["tenant_admin", "accountant"], section: "Reports" },
];
const ADMIN_NAV = [
  { id: "users", to: "/app/users", label: "Users", icon: UserCog, section: "Administration" },
  { id: "branches", to: "/app/branches", label: "Branches", icon: Building2, section: "Administration" },
  { id: "estimating", to: "/app/estimating", label: "Estimating settings", icon: SlidersHorizontal, cap: "estimating", section: "Administration" },
  { id: "settings", to: "/app/settings", label: "Settings", icon: SettingsIcon, end: true, section: "Administration" },
  { id: "data-import", to: "/app/settings/import", label: "Data import", icon: Upload, section: "Administration" },
  { id: "billing", to: "/app/billing", label: "Billing", icon: CreditCard, section: "Administration" },
  { id: "audit", to: "/app/audit", label: "Audit trail", icon: History, section: "Administration" },
];
const PLATFORM_NAV = [
  { id: "platform-overview", to: "/platform", end: true, label: "Overview", icon: LayoutDashboard },
  { id: "platform-tenants", to: "/platform/tenants", label: "Tenants", icon: Building2 },
  { id: "platform-invoices", to: "/platform/invoices", label: "Subscription invoices", icon: Receipt },
  { id: "platform-plans", to: "/platform/plans", label: "Plans & add-ons", icon: Layers },
  { id: "platform-internal", to: "/platform/internal", label: "Internal team", icon: ShieldCheck },
  { id: "platform-audit", to: "/platform/audit", label: "Audit log", icon: History },
  { id: "platform-tickets", to: "/platform/tickets", label: "Support tickets", icon: LifeBuoy },
  { id: "platform-sla", to: "/platform/sla", label: "SLA targets", icon: Timer },
  { id: "platform-settings", to: "/platform/settings", label: "Settings & logs", icon: Cog },
];
const TENANT_ROLES = ["tenant_admin", "accountant", "sales", "production", "purchasing", "viewer", "hr_manager"];

function Cap({ c, children }) {
  const { user } = useAuth();
  if (user?.tenant?.capabilities?.includes(c)) return children;
  return (
    <div className="po-card mx-auto mt-10 max-w-lg p-8 text-center" data-testid="not-enabled-message">
      <h2 className="text-xl font-semibold text-slate-900">Not enabled on your plan</h2>
      <p className="mt-2 text-sm text-slate-500">The {c.replace("_", " ")} module is not part of this workspace's subscription. A workspace admin can upgrade from Billing.</p>
    </div>
  );
}

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function TenantShell() {
  const { user } = useAuth();
  const caps = user?.tenant?.capabilities || [];
  const nav = (user?.role === "tenant_admin" ? [...TENANT_NAV, ...ADMIN_NAV] : TENANT_NAV).filter((n) => (!n.cap || caps.includes(n.cap)) && (!n.roles || n.roles.includes(user?.role)));
  return <AppShell nav={nav} contextLabel="Workspace" />;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return null;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/account/confirm" element={<AccountConfirm />} />
          <Route path="/platform" element={<Guard roles={["platform_admin"]}><AppShell nav={PLATFORM_NAV} contextLabel="Platform admin" /></Guard>}>
            <Route index element={<PlatformDashboard />} />
            <Route path="tenants" element={<PlatformTenants />} />
            <Route path="tenants/:id" element={<PlatformTenantDetail />} />
            <Route path="invoices" element={<PlatformInvoices />} />
            <Route path="plans" element={<PlatformPlans />} />
            <Route path="settings" element={<PlatformSettings />} />
            <Route path="internal" element={<InternalTeam />} />
            <Route path="audit" element={<PlatformAudit />} />
            <Route path="tickets" element={<SupportQueue base="/platform/tickets" />} />
            <Route path="tickets/:id" element={<SupportQueue base="/platform/tickets" />} />
            <Route path="sla" element={<SlaSettings />} />
          </Route>
          <Route path="/ops" element={<Guard roles={INTERNAL_ROLES}><AppShell nav={OPS_NAV} contextLabel="Platform operations" /></Guard>}>
            <Route index element={<OpsConsole />} />
            <Route path="tickets" element={<SupportQueue />} />
            <Route path="tickets/:id" element={<SupportQueue />} />
            <Route path="sla" element={<SlaSettings />} />
          </Route>
          <Route path="/app" element={<Guard roles={TENANT_ROLES}><TenantShell /></Guard>}>
            <Route index element={<TenantDashboard />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerHistory />} />
            <Route path="crm" element={<Cap c="crm"><Crm /></Cap>} />
            <Route path="crm/leads/:id" element={<Cap c="crm"><LeadDetail /></Cap>} />
            <Route path="crm/opportunities/:id" element={<Cap c="crm"><OpportunityDetail /></Cap>} />
            <Route path="quotes" element={<Cap c="estimating"><Quotes /></Cap>} />
            <Route path="quotes/new" element={<Cap c="estimating"><Guard roles={["tenant_admin", "accountant", "sales"]}><QuoteBuilder /></Guard></Cap>} />
            <Route path="quotes/:id" element={<Cap c="estimating"><QuoteDetail /></Cap>} />
            <Route path="quotes/:id/revise" element={<Cap c="estimating"><Guard roles={["tenant_admin", "accountant", "sales"]}><QuoteBuilder /></Guard></Cap>} />
            <Route path="jobs" element={<Cap c="operations"><Jobs /></Cap>} />
            <Route path="jobs/:id" element={<Cap c="operations"><JobDetail /></Cap>} />
            <Route path="capacity" element={<Cap c="operations"><Capacity /></Cap>} />
            <Route path="inventory" element={<Cap c="inventory"><Inventory /></Cap>} />
            <Route path="hr/employees" element={<Cap c="hr"><Employees /></Cap>} />
            <Route path="hr/leave" element={<Cap c="hr"><Leave /></Cap>} />
            <Route path="hr/attendance" element={<Cap c="hr"><Attendance /></Cap>} />
            <Route path="hr/org-chart" element={<Cap c="hr"><OrgChart /></Cap>} />
            <Route path="payroll" element={<Cap c="payroll"><Payroll /></Cap>} />
            <Route path="procurement" element={<Cap c="procurement"><Procurement /></Cap>} />
            <Route path="maintenance" element={<Cap c="maintenance"><Maintenance /></Cap>} />
            <Route path="maintenance/assets/:id" element={<Cap c="maintenance"><AssetDetail /></Cap>} />
            <Route path="insights" element={<Navigate to="/app" replace />} />
            <Route path="estimating" element={<Cap c="estimating"><Estimating /></Cap>} />
            <Route path="templates" element={<Templates />} />
            <Route path="payments" element={<Payments />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="tickets/:id" element={<Tickets />} />
            <Route path="accounting/*" element={<Cap c="accounting"><Accounting /></Cap>} />
            <Route path="reports" element={<Guard roles={["tenant_admin", "accountant", "sales", "production", "purchasing", "viewer", "hr_manager"]}><Cap c="reporting"><Reports /></Cap></Guard>} />
            <Route path="users" element={<Guard roles={["tenant_admin"]}><Users /></Guard>} />
            <Route path="branches" element={<Branches />} />
            <Route path="settings" element={<Guard roles={["tenant_admin"]}><Settings /></Guard>} />
            <Route path="settings/import" element={<Guard roles={["tenant_admin"]}><DataImport /></Guard>} />
            <Route path="settings/import/quickbooks" element={<Guard roles={["tenant_admin"]}><QuickBooksMigration /></Guard>} />
            <Route path="billing" element={<Guard roles={["tenant_admin"]}><Billing /></Guard>} />
            <Route path="billing/checkout/:reference" element={<Guard roles={["tenant_admin"]}><PaystackCheckout /></Guard>} />
            <Route path="billing/callback" element={<BillingCallback />} />
            <Route path="audit" element={<Guard roles={["tenant_admin"]}><AuditLog /></Guard>} />
          </Route>
          <Route path="/link/:token" element={<PortalLink />} />
          <Route path="/portal/login" element={<PortalAuthProvider><PortalLogin /></PortalAuthProvider>} />
          <Route path="/portal/set-password" element={<PortalAuthProvider><PortalSetPassword /></PortalAuthProvider>} />
          <Route path="/portal/verify-email" element={<PortalVerifyEmail />} />
          <Route path="/portal" element={<PortalAuthProvider><PortalLayout /></PortalAuthProvider>}>
            <Route index element={<PortalJobs />} />
            <Route path="jobs/:id" element={<PortalJob />} />
            <Route path="quotes" element={<PortalQuotes />} />
            <Route path="invoices" element={<PortalInvoices />} />
            <Route path="invoices/:id" element={<PortalInvoice />} />
            <Route path="profile" element={<PortalProfile />} />
          </Route>
          <Route path="/floor" element={<Guard roles={TENANT_ROLES}><Cap c="operations"><Floor /></Cap></Guard>} />
          <Route path="/pos" element={<Guard roles={TENANT_ROLES}><Cap c="pos"><Pos /></Cap></Guard>} />
          <Route path="/track/:token" element={<Track />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
